import { useCallback, useEffect, useMemo, useState } from "react";
import { useTableSort, SortTh } from "@/lib/tableSort";

import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Eye, Link2 } from "lucide-react";
import { ArahkanButton } from "@/components/network/ArahkanButton";
import { DeleteInfraButton } from "@/components/network/DeleteInfraButton";
import { ArchiveInfraButton } from "@/components/network/ArchiveInfraButton";
import { SplitterRelations } from "@/components/network/SplitterRelations";
import { InfraFormDialog } from "@/components/network/InfraFormDialog";
import { WilayahMapDialog } from "@/components/network/WilayahMapDialog";
import { StatusPill, NoCoordBadge, PortBar, LocCell, RelocateHint } from "@/components/network/StatusPill";
import { ServerInfo } from "@/components/network/ServerInfo";

import { WilayahCombobox } from "@/components/network/WilayahCombobox";
import { useNetworkRefs, canEditInfra, INFRA_STATUS, STATUS_LABEL, SELECT_CLS } from "@/lib/network";
import { OldKodeLabel } from "@/components/network/OldKodeLabel";
import { InfraCards, InfraCard } from "@/components/network/InfraCards";

export function InfraFilters({ refs, f, setF, prefix, children, placeholder = "Cari kode, nama, alamat…" }) {
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${prefix}-filters`}>
      <Input value={f.q} onChange={set("q")} placeholder={placeholder} className="h-9 w-56" data-testid={`${prefix}-filter-q`} />
      <select className={SELECT_CLS} value={f.lokasi_id} onChange={set("lokasi_id")} data-testid={`${prefix}-filter-lokasi`}>
        <option value="">Semua Lokasi</option>
        {(refs?.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
      <WilayahCombobox className="w-44" options={refs?.wilayah || []} value={f.wilayah_id} allLabel="Semua Wilayah"
                       onChange={(v) => set("wilayah_id")({ target: { value: v } })} testid={`${prefix}-filter-wilayah`} />
      <select className={SELECT_CLS} value={f.status} onChange={set("status")} data-testid={`${prefix}-filter-status`}>
        <option value="">Semua Status</option>
        {INFRA_STATUS.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
      </select>
      {children}
    </div>
  );
}

export function ScopeBanner({ me }) {
  if (!me || !me.can_manage || me.scope_status === "all" || me.scope_status === "ok") return null;
  return (
    <div className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] p-3 text-sm text-[#92400E]" data-testid="network-scope-banner">
      {me.scope_status === "no_wilayah"
        ? "Akun Anda belum memiliki wilayah kewenangan. Hubungi Admin untuk mengatur wilayah di Manajemen User."
        : "Wilayah kewenangan Anda belum dipetakan ke wilayah Billing. Admin perlu mengisi Pemetaan Wilayah di Master ODC."}
      {" "}Anda tetap dapat melihat data, tetapi belum dapat mengelola penempatan pelanggan pada ODP.
    </div>
  );
}

function OdcDetail({ odcId, me, onClose, onDeleted }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (odcId) api.get(`/network/odc/${odcId}`).then((r) => setD(r.data)); }, [odcId]);
  return (
    <Dialog open={!!odcId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[calc(100dvh-5.5rem)] overflow-y-auto max-lg:top-[calc(50%+2rem)] lg:max-h-[92vh]" data-testid="odc-detail-dialog">
        <DialogHeader><DialogTitle className="break-words pr-6" data-testid="odc-detail-title">{d ? `${d.kode} — ${d.nama}` : "Memuat…"}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap gap-3"><StatusPill status={d.status} />{(d.in_server || !d.has_coords) && <LocCell row={d} testid="odc-detail-loc" />}
              {d.kapasitas_splitter > 0 && <span>Kapasitas Splitter: {d.kapasitas_splitter}</span>}</div>
            <ServerInfo kind="odc" id={d.id} />
            <RelocateHint show={!d.in_server && !d.has_coords && (d.odps || []).length > 0} testid="odc-relocate-hint" />
            {(d.alamat || d.has_coords) && <div className="text-[#6B7280]" data-testid="odc-detail-address">{[d.alamat, d.has_coords && `${d.latitude}, ${d.longitude}`].filter(Boolean).join(" · ")}</div>}
            {d.catatan && <div className="text-xs">Catatan: {d.catatan}</div>}
            <div className="flex gap-2"><ArchiveInfraButton kind="odc" item={d} me={me} onDone={onDeleted} /><DeleteInfraButton kind="odc" item={d} me={me} onDeleted={onDeleted} /></div>
            <SplitterRelations kind="odc" id={d.id} kode={d.kode} />
            <div className="font-semibold">ODP di bawah ODC ini ({d.odps.length})</div>
            <div className="max-h-72 divide-y overflow-y-auto rounded border" data-testid="odc-detail-odps">
              {d.odps.map((o) => (
                <div key={o.id} className="flex items-center justify-between p-2 text-xs">
                  <span><b>{o.kode}</b> · {o.nama}</span><PortBar used={o.port_terpakai} cap={o.kapasitas_port} />
                </div>
              ))}
              {!d.odps.length && <p className="p-2 text-xs text-[#6B7280]">Belum ada ODP.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function NetworkOdc() {
  const { refs, wilayahName, lokasiName } = useNetworkRefs();
  const [f, setF] = useState({ q: "", lokasi_id: "", wilayah_id: "", status: "", archived: false });
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [wmap, setWmap] = useState(false);
  const load = useCallback(() => api.get("/network/odc", { params: f }).then((r) => setRows(r.data)), [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const getters = useMemo(() => ({ kode: (o) => o.kode, nama: (o) => o.nama, wilayah: (o) => wilayahName(o.wilayah_id), lokasi: (o) => lokasiName(o.lokasi_id),
    splitter: (o) => (o.kapasitas_splitter > 0 ? o.kapasitas_splitter : null), odp: (o) => o.odp_count, status: (o) => o.status }), [wilayahName, lokasiName]);
  const st = useTableSort("odc", rows, getters);
  const me = refs?.me;
  if (me && !me.can_manage) return <Navigate to="/network/map" replace />;
  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="network-odc-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold">Master ODC</h1>
          <p className="text-sm text-[#6B7280]">Optical Distribution Cabinet per wilayah</p></div>
        <div className="flex gap-2">
          {me?.is_admin && <Button variant="outline" onClick={() => setWmap(true)} data-testid="odc-wmap-btn"><Link2 className="mr-1 h-4 w-4" />Pemetaan Wilayah</Button>}
          {me?.can_manage && <Button onClick={() => setForm({})} data-testid="odc-add-btn"><Plus className="mr-1 h-4 w-4" />Tambah ODC</Button>}
        </div>
      </div>
      <ScopeBanner me={me} />
      <InfraFilters refs={refs} f={f} setF={setF} prefix="odc">
        <label className="flex items-center gap-1.5 text-xs" data-testid="odc-filter-archived">
          <input type="checkbox" checked={f.archived} onChange={(e) => setF((x) => ({ ...x, archived: e.target.checked }))} data-testid="odc-filter-archived-toggle" />Tampilkan Arsip</label>
      </InfraFilters>
      <InfraCards prefix="odc" st={st} cols={[["kode", "Kode"], ["nama", "Nama"], ["wilayah", "Wilayah"], ["lokasi", "Lokasi"], ["splitter", "Splitter"], ["odp", "ODP"], ["status", "Status"]]}
                  empty={rows && !rows.length ? (f.archived ? "Tidak ada ODC yang diarsipkan." : "Belum ada ODC.") : null}
                  render={(o) => (
        <InfraCard key={o.id} prefix="odc" row={o} q={f.q}
                   sub={<><span className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</span> · {lokasiName(o.lokasi_id)}</>}
                   info={<>Splitter: {o.kapasitas_splitter > 0 ? o.kapasitas_splitter : "-"} · {o.odp_count} ODP</>}
                   actions={<>
                     <ArahkanButton iconOnly lat={o.latitude} lng={o.longitude} testid={`odc-card-nav-${o.kode}`} />
                     <Button size="icon" variant="ghost" onClick={() => setDetail(o.id)} data-testid={`odc-card-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                     <ArchiveInfraButton iconOnly kind="odc" item={o} me={me} onDone={load} tid={`odc-card-${o.archived ? "restore" : "archive"}-${o.kode}`} />
                     {canEditInfra(me) && !o.archived && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`odc-card-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                     <DeleteInfraButton iconOnly kind="odc" item={o} me={me} onDeleted={load} tid={`odc-card-delete-${o.kode}`} />
                   </>} />)} />
      <div className="hidden overflow-x-auto rounded-lg border border-[#E5E7EB] bg-white md:block">
        <table className="w-full text-sm" data-testid="odc-table">
          <thead className="bg-[#F8F9FA] text-left text-xs uppercase text-[#6B7280]">
            <tr><SortTh className="p-2" col="kode" label="Kode" s={st} testid="odc" /><SortTh col="nama" label="Nama" s={st} testid="odc" />
              <SortTh col="wilayah" label="Wilayah" s={st} testid="odc" /><SortTh col="lokasi" label="Lokasi" s={st} testid="odc" />
              <SortTh col="splitter" label="Splitter" s={st} testid="odc" /><SortTh col="odp" label="ODP" s={st} testid="odc" />
              <SortTh col="status" label="Status" s={st} testid="odc" /><th>Peta</th><th className="text-right pr-2">Aksi</th></tr>
          </thead>
          <tbody>
            {(st.sorted || []).map((o) => (
              <tr key={o.id} className="border-t" data-testid={`odc-row-${o.kode}`}>
                <td className="p-2 font-mono-tab font-semibold">{o.kode}<OldKodeLabel row={o} q={f.q} testid={`odc-oldkode-${o.kode}`} />{o.archived && <span className="ml-1 rounded bg-[#E5E7EB] px-1 text-[10px] font-semibold text-[#374151]" data-testid={`odc-archived-badge-${o.kode}`}>ARSIP</span>}</td><td>{o.nama}</td><td className={o.wilayah_valid === false ? "text-[#B45309]" : ""}>{wilayahName(o.wilayah_id)}</td>
                <td>{lokasiName(o.lokasi_id)}</td><td>{o.kapasitas_splitter > 0 ? o.kapasitas_splitter : "-"}</td><td>{o.odp_count}</td>
                <td><StatusPill status={o.status} /></td><td><LocCell row={o} testid={`odc-nocoord-${o.kode}`} /></td>
                <td className="pr-2 text-right whitespace-nowrap">
                  <ArahkanButton iconOnly lat={o.latitude} lng={o.longitude} testid={`odc-nav-${o.kode}`} />
                  <Button size="icon" variant="ghost" onClick={() => setDetail(o.id)} data-testid={`odc-view-${o.kode}`}><Eye className="h-4 w-4" /></Button>
                  <ArchiveInfraButton iconOnly kind="odc" item={o} me={me} onDone={load} />
                  {canEditInfra(me) && !o.archived && <Button size="icon" variant="ghost" onClick={() => setForm(o)} data-testid={`odc-edit-${o.kode}`}><Pencil className="h-4 w-4" /></Button>}
                  <DeleteInfraButton iconOnly kind="odc" item={o} me={me} onDeleted={load} />
                </td>
              </tr>
            ))}
            {rows && !rows.length && <tr><td colSpan={9} className="p-6 text-center text-[#6B7280]">{f.archived ? "Tidak ada ODC yang diarsipkan." : "Belum ada ODC."}</td></tr>}
          </tbody>
        </table>
      </div>
      <InfraFormDialog kind="odc" open={!!form} editing={form?.id ? form : null} refs={refs} onClose={() => setForm(null)}
                       onSaved={() => { setForm(null); load(); }} />
      <OdcDetail odcId={detail} me={me} onClose={() => setDetail(null)} onDeleted={() => { setDetail(null); load(); }} />
      {me?.is_admin && <WilayahMapDialog open={wmap} refs={refs} onClose={() => setWmap(false)} />}
    </div>
  );
}
